// Basket reconciliation with the current menu (SUSHI-435).
//
// A saved basket (localStorage "PSUSHI_LIST") never expires and keeps the
// price each item had when it was added. Before any price is shown or sent,
// every basket item is matched against what the shop sells right now:
// - matched items take the current catalog price (name and quantity stay),
// - items that are no longer sold are dropped (silently, by owner decision).
//
// Items are matched by id AND name: przytomni-backend hands out ids "into the
// gaps" (libs/suggest-id.ts), so a deleted product's id can later belong to a
// different product. Same id with a different name means the item is gone.

const MENU_LISTS = ["sets", "products", "ingredients"]

function normalizeName(name) {
    return typeof name === "string" ? name.trim() : ""
}

function toPrice(value) {
    if (value === null || value === undefined || value === "") return NaN
    return Number(value)
}

function keyOf(id, name) {
    return `${id}\u0000${normalizeName(name)}`
}

function hasMatchableIdentity(entry) {
    return (
        !!entry &&
        entry.id !== undefined &&
        entry.id !== null &&
        normalizeName(entry.name) !== ""
    )
}

function asList(list) {
    return Array.isArray(list) ? list : []
}

/**
 * True only when every menu list (sets, products, ingredients) is a non-empty
 * array. The basket must never be reconciled against anything else: an empty
 * or failed menu would wipe every saved basket.
 */
export function isMenuUsable(menu) {
    return (
        !!menu &&
        MENU_LISTS.every(
            key => Array.isArray(menu[key]) && menu[key].length > 0
        )
    )
}

/**
 * Everything the shop currently sells: the menu lists the shop shows (sets,
 * visible products, sellable ingredients) plus the carousel offer (which may
 * contain items the menu lists leave out). Pass only
 * a freshly loaded carousel, never the copy saved in localStorage. The
 * carousel goes first so that the menu lists win when the same id + name is
 * in both with different prices — the menu is the fresher, authoritative
 * source.
 */
export function buildCatalog({ sets, products, ingredients, carousel } = {}) {
    return [
        ...asList(carousel),
        ...asList(sets),
        ...asList(products),
        ...asList(ingredients),
    ]
}

/**
 * Reconciles basket items with the current catalog. Pure: the input items
 * (and the objects inside) are never mutated.
 *
 * @param {Array<{id, name, price, quantity}>} items basket items
 * @param {Array<{id, name, price}>} catalog see buildCatalog
 * @returns {{ items: Array, changed: boolean, removed: Array, repriced: Array }}
 *   `changed` is true when any price changed or any item was removed.
 */
export function reconcileBasket(items, catalog) {
    const currentPrices = new Map()
    for (const entry of asList(catalog)) {
        if (!hasMatchableIdentity(entry)) continue
        const price = toPrice(entry.price)
        // An entry without a usable price cannot price a basket item.
        if (!Number.isFinite(price)) continue
        currentPrices.set(keyOf(entry.id, entry.name), price)
    }

    const reconciled = []
    const removed = []
    const repriced = []
    for (const item of asList(items)) {
        const key = hasMatchableIdentity(item)
            ? keyOf(item.id, item.name)
            : null
        if (key === null || !currentPrices.has(key)) {
            removed.push(item)
            continue
        }

        const price = currentPrices.get(key)
        const next = { ...item, price }
        if (toPrice(item.price) !== price) repriced.push(next)
        reconciled.push(next)
    }

    return {
        items: reconciled,
        changed: removed.length > 0 || repriced.length > 0,
        removed,
        repriced,
    }
}
